import DownloadIcon from "@mui/icons-material/Download";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { atomicConfig } from "../../../../../config";
import useCurrency from "../../../../../hooks/useCurrency";
import {
  fetchPpcAnalysis,
  storeAnalyticsSelector,
} from "../../../../../redux/slices/store_analytics";
import { call_api_auth } from "../../../../../utils/services";

const headerCellSx = {
  fontWeight: 700,
  fontSize: "0.7rem",
  textTransform: "uppercase",
  color: "#94a3b8",
  letterSpacing: "0.08em",
  backgroundColor: "#f8fafc",
  borderBottom: "2px solid #e2e8f0",
  py: 1.5,
  whiteSpace: "nowrap",
};

const CAMPAIGN_COLUMNS = [
  { field: "campaign", label: "Campaign", numeric: false },
  { field: "asins", label: "ASINs", numeric: false },
  { field: "spend", label: "Spend", numeric: true },
  { field: "acos", label: "ACOS", numeric: true },
  { field: "impressions", label: "Impr.", numeric: true },
  { field: "clicks", label: "Clicks", numeric: true },
  { field: "ctr", label: "CTR", numeric: true },
  { field: "orders", label: "Orders", numeric: true },
  { field: "sales", label: "Sales", numeric: true },
  { field: "roas", label: "ROAS", numeric: true },
];

const LOWER_IS_BETTER = new Set(["spend", "acos"]);

// Map ad_type codes (from data) to Amazon Ads console URL path segments.
// SP = Sponsored Products, SB/SBV = Sponsored Brands (incl. Video), SD = Sponsored Display.
// SB/SBV campaign manager deep-links don't resolve correctly with the numeric
// campaign_id, so they are intentionally omitted here (link is disabled).
const AD_TYPE_PATH = {
  SP: "sp",
  SD: "sd",
};

const AD_TYPE_LABELS = {
  SP: "Sponsored Products",
  SB: "Sponsored Brands",
  SBV: "Sponsored Brands Video",
  SD: "Sponsored Display",
};

const AD_TYPE_ORDER = ["SP", "SB", "SBV", "SD"];

const normalizeAdType = (adType) => {
  const code = String(adType || "")
    .trim()
    .toUpperCase();
  return code || null;
};

const formatAdTypeLabel = (adType) => {
  const code = normalizeAdType(adType);
  if (!code) return "Unknown";
  const name = AD_TYPE_LABELS[code];
  return name ? `${code} — ${name}` : code;
};

// Amazon Ads console domain per marketplace.
const AMAZON_ADS_DOMAIN = {
  us: "advertising.amazon.com",
  ca: "advertising.amazon.com",
  mx: "advertising.amazon.com",
  br: "advertising.amazon.com",
  uk: "advertising.amazon.co.uk",
  gb: "advertising.amazon.co.uk",
  de: "advertising.amazon.de",
  fr: "advertising.amazon.fr",
  it: "advertising.amazon.it",
  es: "advertising.amazon.es",
  nl: "advertising.amazon.nl",
  se: "advertising.amazon.se",
  pl: "advertising.amazon.pl",
  jp: "advertising.amazon.co.jp",
  au: "advertising.amazon.com.au",
  ae: "advertising.amazon.ae",
  sa: "advertising.amazon.sa",
  in: "advertising.amazon.in",
};

const getCampaignManagerUrl = (adType, campaignId, marketplace) => {
  if (!campaignId) return null;
  const path = AD_TYPE_PATH[String(adType || "").toUpperCase()];
  if (!path) return null;
  const domain =
    AMAZON_ADS_DOMAIN[String(marketplace || "us").toLowerCase()] ||
    "advertising.amazon.com";
  return `https://${domain}/cm/${path}/campaigns/${campaignId}`;
};

// Mirror backend threshold: keep search term rows with current-week
// spend >= $2 OR orders >= 1. Previous-week-only rows end up with a
// zero current-week summary and should be hidden.
const MIN_SPEND = 2;
const MIN_ORDERS = 1;
const passesThreshold = (summary) =>
  (summary?.spend ?? 0) >= MIN_SPEND || (summary?.orders ?? 0) >= MIN_ORDERS;

const sortComparator = (a, b, field, direction) => {
  const aVal = a[field];
  const bVal = b[field];
  if (aVal == null && bVal == null) return 0;
  if (aVal == null) return 1;
  if (bVal == null) return -1;
  if (typeof aVal === "string") {
    return direction === "asc"
      ? aVal.localeCompare(bVal)
      : bVal.localeCompare(aVal);
  }
  return direction === "asc" ? aVal - bVal : bVal - aVal;
};

const formatCurrency = (val, symbol) =>
  val != null
    ? `${symbol}${val.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    : "—";

const formatPercent = (val) => (val != null ? `${val.toFixed(2)}%` : "—");

const formatNumber = (val) => (val != null ? val.toLocaleString() : "—");

const formatDelta = (field, delta, symbol) => {
  if (delta == null) return null;
  const sign = delta > 0 ? "+" : "";
  switch (field) {
    case "spend":
    case "sales":
      return `${sign}${symbol}${delta.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    case "acos":
    case "ctr":
      return `${sign}${delta.toFixed(2)}%`;
    case "roas":
      return `${sign}${delta.toFixed(1)}`;
    case "impressions":
    case "clicks":
    case "orders":
      return `${sign}${delta.toLocaleString()}`;
    default:
      return `${sign}${delta}`;
  }
};

const getDeltaColor = (field, delta) => {
  if (delta == null || delta === 0) return "inherit";
  const isPositive = delta > 0;
  if (LOWER_IS_BETTER.has(field)) {
    return isPositive ? "#ef4444" : "#16a34a";
  }
  return isPositive ? "#16a34a" : "#ef4444";
};

const MetricCell = ({ field, value, delta }) => {
  const { marketplaceCurrencySymbol } = useCurrency();
  let formatted;
  switch (field) {
    case "spend":
    case "sales":
      formatted = formatCurrency(value, marketplaceCurrencySymbol);
      break;
    case "acos":
    case "ctr":
      formatted = formatPercent(value);
      break;
    case "roas":
      formatted = value != null ? value.toFixed(1) : "—";
      break;
    default:
      formatted = formatNumber(value);
  }

  return (
    <TableCell>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
        <Typography variant="body2" fontWeight={600} sx={{}}>
          {formatted}
        </Typography>
        {delta != null && delta !== 0 && (
          <Typography
            variant="caption"
            fontWeight={600}
            sx={{
              color: getDeltaColor(field, delta),
              fontSize: "0.65rem",
              whiteSpace: "nowrap",
            }}
          >
            ({formatDelta(field, delta, marketplaceCurrencySymbol)})
          </Typography>
        )}
      </Box>
    </TableCell>
  );
};

const CampaignRow = ({ row, idx, marketplace }) => {
  const inactive = row.spend == null && row.sales == null && row.clicks == null;
  const rowSx = {
    backgroundColor: inactive
      ? "#f8fafc"
      : idx % 2 === 0
      ? "#ffffff"
      : "#f8fafc",
    opacity: inactive ? 0.45 : 1,
    transition: "background-color 0.12s ease",
    "&:hover": { backgroundColor: inactive ? "#f1f5f9" : "#eff6ff" },
  };

  const campaignUrl = getCampaignManagerUrl(
    row.adType,
    row.campaignId,
    marketplace
  );

  return (
    <TableRow sx={rowSx}>
      <TableCell>
        {campaignUrl ? (
          <Typography
            component="a"
            href={campaignUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            onClick={(e) => e.stopPropagation()}
            sx={{
              color: "#2563eb",
              fontWeight: 500,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {row.campaign || "—"}
          </Typography>
        ) : (
          <Typography
            variant="body2"
            sx={{ color: "#334155", fontWeight: 500 }}
          >
            {row.campaign || "—"}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {(row.asinList || []).map((asin) => (
            <Chip
              key={asin}
              label={asin}
              size="small"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.7rem",
                height: 22,
                backgroundColor: "#f1f5f9",
                color: "#475569",
                fontWeight: 600,
              }}
            />
          ))}
        </Box>
      </TableCell>
      <MetricCell field="spend" value={row.spend} delta={row.spendDelta} />
      <MetricCell field="acos" value={row.acos} delta={row.acosDelta} />
      <MetricCell
        field="impressions"
        value={row.impressions}
        delta={row.impressionsDelta}
      />
      <MetricCell field="clicks" value={row.clicks} delta={row.clicksDelta} />
      <MetricCell field="ctr" value={row.ctr} delta={row.ctrDelta} />
      <MetricCell field="orders" value={row.orders} delta={row.ordersDelta} />
      <MetricCell field="sales" value={row.sales} delta={row.salesDelta} />
      <MetricCell field="roas" value={row.roas} delta={row.roasDelta} />
    </TableRow>
  );
};

const TotalRow = ({ campaigns }) => {
  const totals = useMemo(() => {
    const sum = (field) =>
      campaigns.reduce((acc, r) => acc + (r[field] ?? 0), 0);
    const totalSpend = sum("spend");
    const totalSales = sum("sales");
    const totalImpressions = sum("impressions");
    const totalClicks = sum("clicks");
    const totalOrders = sum("orders");

    const prevSpend = sum("prevSpend");
    const prevSales = sum("prevSales");
    const prevImpressions = sum("prevImpressions");
    const prevClicks = sum("prevClicks");
    const prevOrders = sum("prevOrders");

    const acos = totalSales > 0 ? (totalSpend / totalSales) * 100 : null;
    const prevAcos = prevSales > 0 ? (prevSpend / prevSales) * 100 : null;
    const ctr =
      totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : null;
    const prevCtr =
      prevImpressions > 0 ? (prevClicks / prevImpressions) * 100 : null;
    const roas = totalSpend > 0 ? totalSales / totalSpend : null;
    const prevRoas = prevSpend > 0 ? prevSales / prevSpend : null;

    const hasPrev = campaigns.some((c) => c.prevSpend != null);

    return {
      spend: totalSpend,
      spendDelta: hasPrev ? totalSpend - prevSpend : null,
      acos,
      acosDelta:
        hasPrev && acos != null && prevAcos != null ? acos - prevAcos : null,
      impressions: Math.ceil(totalImpressions),
      impressionsDelta: hasPrev
        ? Math.ceil(totalImpressions) - Math.ceil(prevImpressions)
        : null,
      clicks: Math.ceil(totalClicks),
      clicksDelta: hasPrev
        ? Math.ceil(totalClicks) - Math.ceil(prevClicks)
        : null,
      ctr,
      ctrDelta:
        hasPrev && ctr != null && prevCtr != null ? ctr - prevCtr : null,
      orders: Math.ceil(totalOrders),
      ordersDelta: hasPrev
        ? Math.ceil(totalOrders) - Math.ceil(prevOrders)
        : null,
      sales: totalSales,
      salesDelta: hasPrev ? totalSales - prevSales : null,
      roas,
      roasDelta:
        hasPrev && roas != null && prevRoas != null ? roas - prevRoas : null,
    };
  }, [campaigns]);

  return (
    <TableRow
      sx={{
        backgroundColor: "#e8f0fe",
        borderTop: "2px solid #93c5fd",
        "&:hover": { backgroundColor: "#dbeafe" },
      }}
    >
      <TableCell>
        <Typography variant="body2" sx={{ color: "#1e40af", fontWeight: 800 }}>
          Total
        </Typography>
      </TableCell>
      <TableCell />
      <MetricCell
        field="spend"
        value={totals.spend}
        delta={totals.spendDelta}
      />
      <MetricCell field="acos" value={totals.acos} delta={totals.acosDelta} />
      <MetricCell
        field="impressions"
        value={totals.impressions}
        delta={totals.impressionsDelta}
      />
      <MetricCell
        field="clicks"
        value={totals.clicks}
        delta={totals.clicksDelta}
      />
      <MetricCell field="ctr" value={totals.ctr} delta={totals.ctrDelta} />
      <MetricCell
        field="orders"
        value={totals.orders}
        delta={totals.ordersDelta}
      />
      <MetricCell
        field="sales"
        value={totals.sales}
        delta={totals.salesDelta}
      />
      <MetricCell field="roas" value={totals.roas} delta={totals.roasDelta} />
    </TableRow>
  );
};

const SearchTermRow = ({ searchTerm, campaigns, summary, marketplace }) => {
  const { marketplaceCurrencySymbol } = useCurrency();
  const [open, setOpen] = useState(false);
  const [sortField, setSortField] = useState("spend");
  const [sortDirection, setSortDirection] = useState("desc");

  const handleSort = useCallback((field) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortDirection("desc");
      }
      return field;
    });
  }, []);

  const sortedCampaigns = useMemo(() => {
    return [...campaigns].sort((a, b) =>
      sortComparator(a, b, sortField, sortDirection)
    );
  }, [campaigns, sortField, sortDirection]);

  return (
    <>
      <TableRow
        sx={{
          "& > *": { borderBottom: "unset" },
          borderTop: "1px solid #e2e8f0",
          cursor: "pointer",
          transition: "background-color 0.15s ease",
          "&:hover": { backgroundColor: "#f0f7ff" },
          ...(open && { backgroundColor: "#f0f7ff" }),
        }}
        onClick={() => setOpen(!open)}
      >
        <TableCell sx={{ width: 44, pl: 1.5 }}>
          <IconButton
            size="small"
            sx={{
              color: open ? "#3b82f6" : "#94a3b8",
              transition: "color 0.15s ease",
            }}
          >
            {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography
            fontWeight={700}
            fontSize="0.875rem"
            sx={{ color: "#1e293b" }}
          >
            {searchTerm}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography
            variant="body2"
            sx={{ color: "#64748b", fontWeight: 600 }}
          >
            <Box component="span" sx={{ color: "#3b82f6", fontWeight: 700 }}>
              {campaigns.length}
            </Box>{" "}
            campaign{campaigns.length !== 1 ? "s" : ""}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            Spend{" "}
            <Box component="span" sx={{ color: "#1e293b", fontWeight: 700 }}>
              {formatCurrency(summary.spend, marketplaceCurrencySymbol)}
            </Box>
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            Sales{" "}
            <Box component="span" sx={{ color: "#1e293b", fontWeight: 700 }}>
              {formatCurrency(summary.sales, marketplaceCurrencySymbol)}
            </Box>
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            ROAS{" "}
            <Box component="span" sx={{ color: "#1e293b", fontWeight: 700 }}>
              {summary.roas != null ? `${summary.roas.toFixed(1)}x` : "—"}
            </Box>
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            Orders{" "}
            <Box component="span" sx={{ color: "#1e293b", fontWeight: 700 }}>
              {formatNumber(summary.orders)}
            </Box>
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ p: 0, border: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                mx: 2,
                my: 1.5,
                borderRadius: 1.5,
                overflow: "auto",
                border: "1px solid #e2e8f0",
                boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                maxHeight: "calc(100vh - 200px)",
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {CAMPAIGN_COLUMNS.map((col) => (
                      <TableCell
                        key={col.field}
                        sx={{
                          ...headerCellSx,
                          position: "sticky",
                          top: 0,
                          zIndex: 2,
                        }}
                      >
                        <TableSortLabel
                          active={sortField === col.field}
                          direction={
                            sortField === col.field ? sortDirection : "desc"
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSort(col.field);
                          }}
                          sx={{
                            color: "inherit !important",
                            "& .MuiTableSortLabel-icon": {
                              fontSize: "0.85rem",
                              opacity: sortField === col.field ? 1 : 0.4,
                            },
                          }}
                        >
                          {col.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedCampaigns.map((row, idx) => (
                    <CampaignRow
                      key={`${row.campaign}-${idx}`}
                      row={row}
                      idx={idx}
                      marketplace={marketplace}
                    />
                  ))}
                  <TotalRow campaigns={campaigns} />
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const PpcAnalysis = ({ store, marketplace }) => {
  const dispatch = useDispatch();
  const { ppcAnalysisData, ppcAnalysisLoading, ppcAnalysisErrorMessage } =
    useSelector(storeAnalyticsSelector);

  const [selectedAsins, setSelectedAsins] = useState([]);
  const [selectedAdTypes, setSelectedAdTypes] = useState([]);
  const [productItemsMap, setProductItemsMap] = useState({});
  const [sortField, setSortField] = useState("spend");
  const [sortDirection, setSortDirection] = useState("desc");

  const handleHeaderSort = useCallback((field) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortDirection("desc");
      }
      return field;
    });
  }, []);

  const apiStore = useMemo(
    () => (store ? store.replace("_client", "") : store),
    [store]
  );

  useEffect(() => {
    if (store && store !== "all") {
      dispatch(fetchPpcAnalysis({ store, marketplace: marketplace || "us" }));
    }
  }, [dispatch, store, marketplace]);

  useEffect(() => {
    if (!store || store === "all") return;
    let cancelled = false;
    const fetchItems = async () => {
      try {
        const url = `${atomicConfig.storeManagementServiceUrl}/fetch-product-items-by-asins/${apiStore}`;
        const result = await call_api_auth(
          url,
          "POST",
          JSON.stringify({
            marketplace: marketplace || "us",
          })
        );
        if (!cancelled) {
          const items = result.data?.product_items || [];
          const map = {};
          items.forEach((item) => {
            if (item.asin) map[item.asin] = item;
          });
          setProductItemsMap(map);
        }
      } catch {
        if (!cancelled) setProductItemsMap({});
      }
    };
    fetchItems();
    return () => {
      cancelled = true;
    };
  }, [apiStore, store, marketplace]);

  const availableAsins = useMemo(() => {
    const rowsSrc = Array.isArray(ppcAnalysisData)
      ? ppcAnalysisData
      : ppcAnalysisData && typeof ppcAnalysisData === "object"
      ? Object.values(ppcAnalysisData).every(
          (v) => v && typeof v === "object" && !Array.isArray(v)
        )
        ? Object.values(ppcAnalysisData)
        : Object.values(ppcAnalysisData).find((v) => Array.isArray(v)) || []
      : [];
    const set = new Set();
    for (const row of rowsSrc) {
      const asins = (row?.asins ?? "")
        .split("|")
        .map((a) => a.trim())
        .filter(Boolean);
      asins.forEach((a) => set.add(a));
    }
    return Array.from(set).sort();
  }, [ppcAnalysisData]);

  const availableAdTypes = useMemo(() => {
    const rowsSrc = Array.isArray(ppcAnalysisData)
      ? ppcAnalysisData
      : ppcAnalysisData && typeof ppcAnalysisData === "object"
      ? Object.values(ppcAnalysisData).every(
          (v) => v && typeof v === "object" && !Array.isArray(v)
        )
        ? Object.values(ppcAnalysisData)
        : Object.values(ppcAnalysisData).find((v) => Array.isArray(v)) || []
      : [];
    const set = new Set();
    for (const row of rowsSrc) {
      const code = normalizeAdType(row?.ad_type);
      if (code) set.add(code);
    }
    return Array.from(set).sort((a, b) => {
      const ai = AD_TYPE_ORDER.indexOf(a);
      const bi = AD_TYPE_ORDER.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  }, [ppcAnalysisData]);

  // Normalize: API may return an array, or an object of row objects
  const rows = useMemo(() => {
    if (Array.isArray(ppcAnalysisData)) return ppcAnalysisData;
    if (ppcAnalysisData && typeof ppcAnalysisData === "object") {
      const values = Object.values(ppcAnalysisData);
      if (values.length === 0) return [];
      // Object of objects — each value is a row
      if (values.every((v) => v && typeof v === "object" && !Array.isArray(v)))
        return values;
      // Object wrapping an array
      const arr = values.find((v) => Array.isArray(v));
      if (arr) return arr;
    }
    return [];
  }, [ppcAnalysisData]);

  const weekDates = useMemo(() => {
    /*
      The backend returns rows with week_start_date (the Monday, YYYY-MM-DD).
      Build a Monday-to-Sunday range label for the two most recent weeks,
      e.g. "Oct 19-25", or "Oct 26-Nov 1" when the week crosses a month.
      latest is null when only one week is present.
      The date string is parsed manually to avoid locale/timezone parsing issues.
    */
    if (rows.length === 0) return null;
    const unique = [
      ...new Set(rows.map((row) => row.week_start_date).filter(Boolean)),
    ].sort();
    if (unique.length === 0) return null;

    const MONTHS = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const strToDate = (dateStr) => {
      const [y, m, day] = dateStr.split("-");
      return new Date(Number(y), Number(m) - 1, Number(day));
    };

    const fmtRange = (start) => {
      const end = new Date(
        start.getFullYear(),
        start.getMonth(),
        start.getDate() + 6
      );
      const startLabel = `${MONTHS[start.getMonth()]} ${start.getDate()}`;
      const endLabel =
        end.getMonth() === start.getMonth()
          ? `${end.getDate()}`
          : `${MONTHS[end.getMonth()]} ${end.getDate()}`;
      return `${startLabel}-${endLabel}`;
    };

    const latestStr = unique[unique.length - 1];
    const prevStr = unique.length > 1 ? unique[unique.length - 2] : null;
    return {
      latest: fmtRange(strToDate(latestStr)),
      prev: prevStr ? fmtRange(strToDate(prevStr)) : null,
    };
  }, [rows]);

  const searchTermGroups = useMemo(() => {
    if (rows.length === 0) return [];

    // Find the two most recent distinct week dates
    const allDates = [
      ...new Set(rows.map((row) => row.week_start_date).filter(Boolean)),
    ].sort();
    const latestWeek = allDates[allDates.length - 1];
    const prevWeek = allDates[allDates.length - 2] ?? null;

    const SEP = "\x00";

    // Index all rows by "searchTerm<SEP>campaignName<SEP>weekDate"
    const rowIndex = {};
    for (const row of rows) {
      const key = `${row.search_term?.trim().toLowerCase()}${SEP}${
        row.campaign_name
      }${SEP}${row.week_start_date}`;
      rowIndex[key] = row;
    }

    // Collect unique search_term + campaign_name pairs from ALL weeks
    const pairSet = new Set();
    for (const row of rows) {
      pairSet.add(
        `${row.search_term?.trim().toLowerCase()}${SEP}${row.campaign_name}`
      );
    }

    // Group by search term
    const bySearchTerm = {};
    for (const pair of pairSet) {
      const sepIdx = pair.indexOf(SEP);
      const stKey = pair.slice(0, sepIdx);
      const campName = pair.slice(sepIdx + 1);
      const currRow =
        rowIndex[`${stKey}${SEP}${campName}${SEP}${latestWeek}`] ?? null;
      const prevRow =
        prevWeek != null
          ? rowIndex[`${stKey}${SEP}${campName}${SEP}${prevWeek}`] ?? null
          : null;

      // Use whichever row exists for display name / asins
      const sourceRow = currRow || prevRow;
      if (!sourceRow) continue;

      if (!bySearchTerm[stKey]) {
        bySearchTerm[stKey] = {
          searchTerm: sourceRow.search_term,
          rows: [],
        };
      }

      const asinList = (sourceRow.asins ?? "")
        .split("|")
        .map((a) => a.trim())
        .filter(Boolean);

      const curr = currRow
        ? {
            spend: currRow.total_spend ?? null,
            acos: currRow.acos ?? null,
            impressions:
              currRow.total_impressions != null
                ? Math.ceil(currRow.total_impressions)
                : null,
            clicks:
              currRow.total_clicks != null
                ? Math.ceil(currRow.total_clicks)
                : null,
            ctr: currRow.ctr != null ? currRow.ctr * 100 : null,
            orders:
              currRow.total_orders != null
                ? Math.ceil(currRow.total_orders)
                : null,
            sales: currRow.total_sales ?? null,
            roas: currRow.roas ?? null,
          }
        : {};

      const prev = prevRow
        ? {
            spend: prevRow.total_spend ?? null,
            acos: prevRow.acos ?? null,
            impressions:
              prevRow.total_impressions != null
                ? Math.ceil(prevRow.total_impressions)
                : null,
            clicks:
              prevRow.total_clicks != null
                ? Math.ceil(prevRow.total_clicks)
                : null,
            ctr: prevRow.ctr != null ? prevRow.ctr * 100 : null,
            orders:
              prevRow.total_orders != null
                ? Math.ceil(prevRow.total_orders)
                : null,
            sales: prevRow.total_sales ?? null,
            roas: prevRow.roas ?? null,
          }
        : null;

      const delta = (currField, prevField) =>
        prev && curr[currField] != null && prev[prevField ?? currField] != null
          ? curr[currField] - prev[prevField ?? currField]
          : null;

      bySearchTerm[stKey].rows.push({
        campaign: sourceRow.campaign_name ?? null,
        campaignId: sourceRow.campaign_id ?? null,
        adType: sourceRow.ad_type ?? null,
        asinList,
        spend: curr.spend ?? null,
        acos: curr.acos ?? null,
        impressions: curr.impressions ?? null,
        clicks: curr.clicks ?? null,
        ctr: curr.ctr ?? null,
        orders: curr.orders ?? null,
        sales: curr.sales ?? null,
        roas: curr.roas ?? null,
        // previous week raw values (for TotalRow delta computation)
        prevSpend: prev?.spend ?? null,
        prevAcos: prev?.acos ?? null,
        prevImpressions: prev?.impressions ?? null,
        prevClicks: prev?.clicks ?? null,
        prevCtr: prev?.ctr ?? null,
        prevOrders: prev?.orders ?? null,
        prevSales: prev?.sales ?? null,
        prevRoas: prev?.roas ?? null,
        // deltas
        spendDelta: delta("spend"),
        acosDelta: delta("acos"),
        impressionsDelta: delta("impressions"),
        clicksDelta: delta("clicks"),
        ctrDelta: delta("ctr"),
        ordersDelta: delta("orders"),
        salesDelta: delta("sales"),
        roasDelta: delta("roas"),
      });
    }

    const groups = Object.values(bySearchTerm).map(
      ({ searchTerm, rows: campaigns }) => {
        const totalSpend = campaigns.reduce((s, c) => s + (c.spend ?? 0), 0);
        const totalSales = campaigns.reduce((s, c) => s + (c.sales ?? 0), 0);
        const totalOrders = campaigns.reduce((s, c) => s + (c.orders ?? 0), 0);
        const summary = {
          spend: totalSpend,
          sales: totalSales,
          orders: totalOrders,
          roas: totalSpend > 0 ? totalSales / totalSpend : null,
        };

        return { searchTerm, campaigns, summary, totalSpend };
      }
    );

    const visible = groups.filter((g) => passesThreshold(g.summary));
    visible.sort((a, b) => b.totalSpend - a.totalSpend);
    return visible;
  }, [rows]);

  const filteredSearchTermGroups = useMemo(() => {
    if (selectedAsins.length === 0 && selectedAdTypes.length === 0) {
      return searchTermGroups;
    }
    const selectedAsinSet = new Set(selectedAsins);
    const selectedAdTypeSet = new Set(selectedAdTypes);

    const result = [];
    for (const group of searchTermGroups) {
      const matchingCampaigns = group.campaigns.filter((c) => {
        const asinOk =
          selectedAsinSet.size === 0 ||
          (c.asinList || []).some((asin) => selectedAsinSet.has(asin));
        if (!asinOk) return false;
        if (selectedAdTypeSet.size === 0) return true;
        const code = normalizeAdType(c.adType);
        return code != null && selectedAdTypeSet.has(code);
      });
      if (matchingCampaigns.length === 0) continue;

      const totalSpend = matchingCampaigns.reduce(
        (s, c) => s + (c.spend ?? 0),
        0
      );
      const totalSales = matchingCampaigns.reduce(
        (s, c) => s + (c.sales ?? 0),
        0
      );
      const totalOrders = matchingCampaigns.reduce(
        (s, c) => s + (c.orders ?? 0),
        0
      );

      const summary = {
        spend: totalSpend,
        sales: totalSales,
        orders: totalOrders,
        roas: totalSpend > 0 ? totalSales / totalSpend : null,
      };
      if (!passesThreshold(summary)) continue;

      result.push({
        searchTerm: group.searchTerm,
        campaigns: matchingCampaigns,
        summary,
        totalSpend,
      });
    }

    result.sort((a, b) => b.totalSpend - a.totalSpend);
    return result;
  }, [searchTermGroups, selectedAsins, selectedAdTypes]);

  const sortedSearchTermGroups = useMemo(() => {
    const getValue = (group) => {
      switch (sortField) {
        case "searchTerm":
          return group.searchTerm ?? "";
        case "campaigns":
          return group.campaigns.length;
        case "spend":
          return group.summary.spend ?? 0;
        case "sales":
          return group.summary.sales ?? 0;
        case "roas":
          return group.summary.roas ?? null;
        case "orders":
          return group.summary.orders ?? 0;
        default:
          return 0;
      }
    };

    return [...filteredSearchTermGroups].sort((a, b) => {
      const aVal = getValue(a);
      const bVal = getValue(b);
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === "string") {
        return sortDirection === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
    });
  }, [filteredSearchTermGroups, sortField, sortDirection]);

  const handleDownloadCsv = useCallback(() => {
    const escapeCell = (val) => {
      const str = val == null ? "" : String(val);
      return str.includes(",") || str.includes('"') || str.includes("\n")
        ? `"${str.replace(/"/g, '""')}"`
        : str;
    };

    const rows = [];
    rows.push(
      [
        "Search Term",
        "Campaign",
        "ASINs",
        "Spend",
        "ACOS",
        "Impressions",
        "Clicks",
        "CTR",
        "Orders",
        "Sales",
        "ROAS",
      ].map(escapeCell)
    );

    sortedSearchTermGroups.forEach(({ searchTerm, campaigns }) => {
      campaigns.forEach((c) => {
        rows.push([
          escapeCell(searchTerm),
          escapeCell(c.campaign || ""),
          escapeCell((c.asinList || []).join(", ")),
          escapeCell(c.spend?.toFixed(2) ?? ""),
          escapeCell(c.acos != null ? `${c.acos.toFixed(2)}%` : ""),
          escapeCell(c.impressions ?? ""),
          escapeCell(c.clicks ?? ""),
          escapeCell(c.ctr != null ? `${c.ctr.toFixed(2)}%` : ""),
          escapeCell(c.orders ?? ""),
          escapeCell(c.sales?.toFixed(2) ?? ""),
          escapeCell(c.roas != null ? c.roas.toFixed(2) : ""),
        ]);
      });
    });

    const csvContent = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `ppc_analysis_${store}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [sortedSearchTermGroups, store]);

  if (ppcAnalysisLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (ppcAnalysisErrorMessage) {
    return (
      <Box py={4} textAlign="center">
        <Typography color="error">{ppcAnalysisErrorMessage}</Typography>
      </Box>
    );
  }

  if (searchTermGroups.length === 0 && !ppcAnalysisLoading) {
    return (
      <Box py={4} textAlign="center">
        <Typography color="text.secondary">
          {store && store !== "all"
            ? "No PPC analysis data available"
            : "Please select a store"}
        </Typography>
      </Box>
    );
  }

  const productPicker = (
    <Autocomplete
      multiple
      size="small"
      options={availableAsins}
      value={selectedAsins}
      onChange={(_, newValue) => setSelectedAsins(newValue)}
      disableCloseOnSelect
      getOptionLabel={(option) => {
        const item = productItemsMap[option];
        return item?.product_name ? `${option} - ${item.product_name}` : option;
      }}
      renderOption={(props, option, { selected }) => {
        const item = productItemsMap[option];
        return (
          <li {...props}>
            <Checkbox size="small" checked={selected} sx={{ mr: 0.5 }} />
            {item?.image && (
              <Box
                component="img"
                src={item.image}
                alt={option}
                sx={{
                  width: 32,
                  height: 32,
                  objectFit: "contain",
                  mr: 1,
                  borderRadius: 0.5,
                  flexShrink: 0,
                }}
              />
            )}
            <Box sx={{ overflow: "hidden" }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                {option}
              </Typography>
              {item?.product_name && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ display: "block", maxWidth: 300 }}
                >
                  {item.product_name}
                </Typography>
              )}
            </Box>
          </li>
        );
      }}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const item = productItemsMap[option];
          return (
            <Chip
              size="small"
              avatar={
                item?.image ? (
                  <Box
                    component="img"
                    src={item.image}
                    alt={option}
                    sx={{
                      width: 20,
                      height: 20,
                      objectFit: "contain",
                      borderRadius: "50%",
                    }}
                  />
                ) : undefined
              }
              label={option}
              {...getTagProps({ index })}
            />
          );
        })
      }
      filterOptions={(options, { inputValue }) => {
        const lower = inputValue.toLowerCase();
        return options.filter((asin) => {
          if (asin.toLowerCase().includes(lower)) return true;
          const item = productItemsMap[asin];
          return item?.product_name?.toLowerCase().includes(lower);
        });
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={selectedAsins.length === 0 ? "All Products" : ""}
        />
      )}
      sx={{ minWidth: 350 }}
    />
  );

  const campaignTypePicker = (
    <Autocomplete
      multiple
      size="small"
      options={availableAdTypes}
      value={selectedAdTypes}
      onChange={(_, newValue) => setSelectedAdTypes(newValue)}
      disableCloseOnSelect
      getOptionLabel={formatAdTypeLabel}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          <Checkbox size="small" checked={selected} sx={{ mr: 0.5 }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {option}
            </Typography>
            {AD_TYPE_LABELS[option] && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block" }}
              >
                {AD_TYPE_LABELS[option]}
              </Typography>
            )}
          </Box>
        </li>
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip size="small" label={option} {...getTagProps({ index })} />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={selectedAdTypes.length === 0 ? "All Campaign Types" : ""}
        />
      )}
      sx={{ minWidth: 220 }}
    />
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{
            color: "#0f172a",
            fontSize: "1rem",
            letterSpacing: "-0.01em",
          }}
        >
          {weekDates
            ? weekDates.prev
              ? `Weekly PPC Analysis: week (${weekDates.latest}) over week of (${weekDates.prev})`
              : `Weekly PPC Analysis: week (${weekDates.latest})`
            : "Weekly PPC Analysis"}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {campaignTypePicker}
          {productPicker}
          <Button
            startIcon={<DownloadIcon style={{ fontSize: "18px" }} />}
            onClick={handleDownloadCsv}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              textTransform: "none",
              fontSize: "13px",
              fontWeight: 600,
              padding: "8px 16px",
              borderRadius: "8px",
              backgroundColor: "#10b981",
              color: "#ffffff",
              boxShadow: "0 2px 4px rgba(16, 185, 129, 0.3)",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: "#059669",
                transform: "translateY(-1px)",
                boxShadow: "0 4px 8px rgba(16, 185, 129, 0.4)",
              },
            }}
          >
            Download as CSV
          </Button>
        </Box>
      </Box>
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2,
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
        elevation={0}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellSx, width: 44, pl: 1.5 }} />
              {[
                { field: "searchTerm", label: "Search Term" },
                { field: "campaigns", label: "Campaigns" },
                { field: "spend", label: "Spend" },
                { field: "sales", label: "Sales" },
                { field: "roas", label: "ROAS" },
                { field: "orders", label: "Orders" },
              ].map((col) => (
                <TableCell key={col.field} sx={headerCellSx}>
                  <TableSortLabel
                    active={sortField === col.field}
                    direction={sortField === col.field ? sortDirection : "desc"}
                    onClick={() => handleHeaderSort(col.field)}
                    sx={{
                      color: "inherit !important",
                      "& .MuiTableSortLabel-icon": {
                        fontSize: "0.85rem",
                        opacity: sortField === col.field ? 1 : 0.4,
                      },
                    }}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedSearchTermGroups.map(
              ({ searchTerm, campaigns, summary }) => (
                <SearchTermRow
                  key={searchTerm}
                  searchTerm={searchTerm}
                  campaigns={campaigns}
                  summary={summary}
                  marketplace={marketplace}
                />
              )
            )}
            {sortedSearchTermGroups.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} sx={{ textAlign: "center", py: 4 }}>
                  <Typography color="text.secondary">
                    No search terms match the selected filters
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PpcAnalysis;
